import React, { Component, createRef } from "react";
import { Form, Row, Col, FormGroup, Label } from "reactstrap";
import { LoadingDots, createTabHeaderTemplate } from "../../components/ui";
import { TabPanel, TabView } from "primereact/tabview";
import * as Yup from "yup";
import { toastService } from "../../services/toastService";
import { merchantConfigrationsApiService } from "../../services/api/merchants/MerchantConfigrations";
import {
    GetMerchantConfigrationResponse,
    MerchantBoricaConfigurationKeys,
    MerchantBoricaConfigurationRequest,
} from "../../models/MerchantModel";
import { Formik, ErrorMessage, FormikProps } from "formik";
import { utils } from "../../utils/utils";
import { InputText } from "primereact/inputtext";
import { isSuperAdmin } from "../../authGuard";
import { Button } from "primereact/button";
import GeneralConfiguration from "./GeneralConfiguration";

interface MerchantConfigrationProps {
    merchantId: number;
    isExternalKyc: boolean;
}

interface MerchantConfigrationState {
    isLoading: boolean;
    merchantConfigrations: GetMerchantConfigrationResponse[];
    inititalValues: formikKeys;
    activeTabIndex: number;
}

type formikKeys = {
    identificationCallbackURL: string;
    identificationCancelURL: string;
    identificationSuccessURL: string;
    documentSignSuccessURL: string;
    documentSignFailedURL: string;
    btrustAppRelyingPartyId: string;
    webIdentificationRelyingPartyId: string;
};

const validationSchema = Yup.object().shape({
    documentSignSuccessURL: Yup.string().required("Document Sign Success URL is required"),
    documentSignFailedURL: Yup.string().required("Document Sign Failed URL is required"),
    identificationSuccessURL: Yup.string().required("Success URL is required"),
    identificationCancelURL: Yup.string().required("Cancel URL is required"),
    identificationCallbackURL: Yup.string().required("Callback URL is required"),
    btrustAppRelyingPartyId: Yup.string().when([], {
        is: () => isSuperAdmin(),
        then: (schema) => schema.required("BTrust App RelyingParty Id is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    webIdentificationRelyingPartyId: Yup.string().when([], {
        is: () => isSuperAdmin(),
        then: (schema) => schema.required("Web Identification Relying Party Id is required"),
        otherwise: (schema) => schema.notRequired(),
    }),
});

export default class MerchantConfigration extends Component<MerchantConfigrationProps, MerchantConfigrationState> {
    formikRef = createRef<FormikProps<formikKeys>>();

    constructor(props: MerchantConfigrationProps | Readonly<MerchantConfigrationProps>) {
        super(props);

        this.state = {
            isLoading: true,
            merchantConfigrations: [],
            inititalValues: {
                identificationCallbackURL: "",
                identificationCancelURL: "",
                identificationSuccessURL: "",
                btrustAppRelyingPartyId: "",
                webIdentificationRelyingPartyId: "",
                documentSignFailedURL: "",
                documentSignSuccessURL: "",
            },
            activeTabIndex: 0,
        };
    }

    async componentDidMount() {
        await this.loadMerchantConfigration();
    }

    loadMerchantConfigration = async () => {
        if (this.props.merchantId) {
            await this.MerchantConfigrationData();
        }
        this.setState({ isLoading: false });
    };

    MerchantConfigrationData = async () => {
        const configResult = await merchantConfigrationsApiService.getMerchantConfigration(this.props.merchantId);
        if (!configResult.success) {
            toastService.error(configResult.message);
            this.setState({ isLoading: false });
            return;
        }
        const formData = {
            identificationCallbackURL:
                configResult.data.find((item) => item.key === MerchantBoricaConfigurationKeys.IdentificationCallbackURL)?.value ?? "",
            identificationCancelURL:
                configResult.data.find((item) => item.key === MerchantBoricaConfigurationKeys.IdentificationCancelURL)?.value ?? "",
            identificationSuccessURL:
                configResult.data.find((item) => item.key === MerchantBoricaConfigurationKeys.IdentificationSuccessURL)?.value ?? "",
            documentSignSuccessURL:
                configResult.data.find((item) => item.key === MerchantBoricaConfigurationKeys.DocumentSignSuccessURL)?.value ?? "",
            documentSignFailedURL:
                configResult.data.find((item) => item.key === MerchantBoricaConfigurationKeys.DocumentSignFailedURL)?.value ?? "",
            ...(isSuperAdmin() && {
                btrustAppRelyingPartyId:
                    configResult.data.find((item) => item.key === MerchantBoricaConfigurationKeys.BtrustAppRelyingPartyId)?.value ?? "",
                webIdentificationRelyingPartyId:
                    configResult.data.find((item) => item.key === MerchantBoricaConfigurationKeys.WebIdentificationRelyingPartyId)?.value ?? "",
            }),
        } as formikKeys;

        this.setState({ merchantConfigrations: configResult.data, inititalValues: formData });
    };

    handleSubmit = async (values: formikKeys) => {
        this.setState({ isLoading: true });
        const invalidKeys: string[] = [];
        const configurations: { [key: string]: string } = {};

        Object.entries(values).forEach(([key, value]) => {
            const normalizedKey = key.toLowerCase();
            const isValidKey = Object.values(MerchantBoricaConfigurationKeys)
                .map((k) => k.toLowerCase())
                .includes(normalizedKey);

            if (!isValidKey) {
                invalidKeys.push(key);
            } else {
                const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);
                configurations[formattedKey] = value;
            }
        });

        if (invalidKeys.length > 0) {
            this.setState({ isLoading: false });
            toastService.error(`Invalid key(s): ${invalidKeys.join(", ")}`);
            return;
        }

        if (isSuperAdmin()) {
            configurations[MerchantBoricaConfigurationKeys.BtrustAppRelyingPartyId] = values.btrustAppRelyingPartyId;
            configurations[MerchantBoricaConfigurationKeys.WebIdentificationRelyingPartyId] = values.webIdentificationRelyingPartyId;
        }
        const request: MerchantBoricaConfigurationRequest = {
            configurations: configurations,
        };

        const result = await merchantConfigrationsApiService.saveMerchantConfigration(this.props.merchantId, request);
        if (!result.success) {
            this.setState({ isLoading: false });
            toastService.error(result.message);
            return;
        }
        this.setState({ isLoading: true });
        await this.MerchantConfigrationData();
        this.setState({ isLoading: false });
        toastService.success("Configrations updated successfully.");
    };

    render() {
        return (
            <div>
                    {this.state.isLoading ? (
                        <LoadingDots />
                    ) : (
                            <TabView
                                activeIndex={this.state.activeTabIndex}
                                onTabChange={(e) => this.setState({ activeTabIndex: e.index })}
                            >
                                {this.props.isExternalKyc && (
                                    <TabPanel
                                        header="Borica Configurations"
                                        pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                        headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                    >
                                        <Formik
                                            initialValues={this.state.inititalValues}
                                            validationSchema={validationSchema}
                                            validateOnChange={true}
                                            validateOnBlur={true}
                                            onSubmit={async (values, { setSubmitting }) => {
                                                await this.handleSubmit(values);
                                                setSubmitting(false);
                                            }}
                                        >
                                            {({ values, setFieldValue, errors, touched, handleSubmit }) => (
                                                <Form>
                                                    <Row>
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label for="IdentificationSuccessURL">
                                                                    {utils.formatMerchnatBoricaConfigrationKey(
                                                                        MerchantBoricaConfigurationKeys.IdentificationSuccessURL,
                                                                    )}
                                                                </Label>
                                                                <InputText
                                                                    style={{ height: "45px" }}
                                                                    required={true}
                                                                    value={values.identificationSuccessURL}
                                                                    onChange={(e) => setFieldValue("identificationSuccessURL", e.target.value)}
                                                                    name="identificationSuccessURL"
                                                                    className={`form-control ${errors.identificationSuccessURL && touched.identificationSuccessURL ? "is-invalid" : ""}`}
                                                                />
                                                                <ErrorMessage
                                                                    name="identificationSuccessURL"
                                                                    component="div"
                                                                    className="invalid-feedback"
                                                                />
                                                            </FormGroup>
                                                        </Col>

                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label for="IdentificationCancelURL">
                                                                    {utils.formatMerchnatBoricaConfigrationKey(
                                                                        MerchantBoricaConfigurationKeys.IdentificationCancelURL,
                                                                    )}
                                                                </Label>
                                                                <InputText
                                                                    style={{ height: "45px" }}
                                                                    required={true}
                                                                    value={values.identificationCancelURL}
                                                                    onChange={(e) => setFieldValue("identificationCancelURL", e.target.value)}
                                                                    name="identificationCancelURL"
                                                                    className={`form-control ${errors.identificationCancelURL && touched.identificationCancelURL ? "is-invalid" : ""}`}
                                                                />
                                                                <ErrorMessage
                                                                    name="identificationCancelURL"
                                                                    component="div"
                                                                    className="invalid-feedback"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    <Row>
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label for="DocumentSignFailedURL">
                                                                    {utils.formatMerchnatBoricaConfigrationKey(
                                                                        MerchantBoricaConfigurationKeys.DocumentSignFailedURL,
                                                                    )}
                                                                </Label>
                                                                <InputText
                                                                    style={{ height: "45px" }}
                                                                    required={true}
                                                                    value={values.documentSignFailedURL}
                                                                    onChange={(e) => setFieldValue("documentSignFailedURL", e.target.value)}
                                                                    name="documentSignFailedURL"
                                                                    className={`form-control ${errors.documentSignFailedURL && touched.documentSignFailedURL ? "is-invalid" : ""}`}
                                                                />
                                                                <ErrorMessage
                                                                    name="documentSignFailedURL"
                                                                    component="div"
                                                                    className="invalid-feedback"
                                                                />
                                                            </FormGroup>
                                                        </Col>

                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label for="DocumentSignSuccessURL">
                                                                    {utils.formatMerchnatBoricaConfigrationKey(
                                                                        MerchantBoricaConfigurationKeys.DocumentSignSuccessURL,
                                                                    )}
                                                                </Label>
                                                                <InputText
                                                                    style={{ height: "45px" }}
                                                                    required={true}
                                                                    value={values.documentSignSuccessURL}
                                                                    onChange={(e) => setFieldValue("documentSignSuccessURL", e.target.value)}
                                                                    name="documentSignSuccessURL"
                                                                    className={`form-control ${errors.documentSignSuccessURL && touched.documentSignSuccessURL ? "is-invalid" : ""}`}
                                                                />
                                                                <ErrorMessage
                                                                    name="documentSignSuccessURL"
                                                                    component="div"
                                                                    className="invalid-feedback"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>

                                                    <Row>
                                                        <Col md={6}>
                                                            <FormGroup>
                                                                <Label for="IdentificationCallbackURL">
                                                                    {utils.formatMerchnatBoricaConfigrationKey(
                                                                        MerchantBoricaConfigurationKeys.IdentificationCallbackURL,
                                                                    )}
                                                                </Label>
                                                                <InputText
                                                                    style={{ height: "45px" }}
                                                                    required={true}
                                                                    value={values.identificationCallbackURL}
                                                                    onChange={(e) => setFieldValue("identificationCallbackURL", e.target.value)}
                                                                    name="identificationCallbackURL"
                                                                    className={`form-control ${errors.identificationCallbackURL && touched.identificationCallbackURL ? "is-invalid" : ""}`}
                                                                />
                                                                <ErrorMessage
                                                                    name="identificationCallbackURL"
                                                                    component="div"
                                                                    className="invalid-feedback"
                                                                />
                                                            </FormGroup>
                                                        </Col>

                                                        {isSuperAdmin() && (
                                                            <Col md={6}>
                                                                <FormGroup>
                                                                    <Label for="BtrustAppRelyingPartyId">
                                                                        {utils.formatMerchnatBoricaConfigrationKey(
                                                                            MerchantBoricaConfigurationKeys.BtrustAppRelyingPartyId,
                                                                        )}
                                                                    </Label>
                                                                    <InputText
                                                                        style={{ height: "45px" }}
                                                                        value={values.btrustAppRelyingPartyId}
                                                                        onChange={(e) => setFieldValue("btrustAppRelyingPartyId", e.target.value)}
                                                                        name="btrustAppRelyingPartyId"
                                                                        className={`form-control ${errors.btrustAppRelyingPartyId && touched.btrustAppRelyingPartyId ? "is-invalid" : ""}`}
                                                                        readOnly={!isSuperAdmin()}
                                                                    />
                                                                    <ErrorMessage
                                                                        name="btrustAppRelyingPartyId"
                                                                        component="div"
                                                                        className="invalid-feedback"
                                                                    />
                                                                </FormGroup>
                                                            </Col>
                                                        )}
                                                    </Row>

                                                    <Row>
                                                        {isSuperAdmin() && (
                                                            <Col md={6}>
                                                                <FormGroup>
                                                                    <Label for="WebIdentificationRelyingPartyId">
                                                                        {utils.formatMerchnatBoricaConfigrationKey(
                                                                            MerchantBoricaConfigurationKeys.WebIdentificationRelyingPartyId,
                                                                        )}
                                                                    </Label>
                                                                    <InputText
                                                                        style={{ height: "45px" }}
                                                                        value={values.webIdentificationRelyingPartyId}
                                                                        onChange={(e) =>
                                                                            setFieldValue("webIdentificationRelyingPartyId", e.target.value)
                                                                        }
                                                                        name="webIdentificationRelyingPartyId"
                                                                        className={`form-control ${errors.webIdentificationRelyingPartyId && touched.webIdentificationRelyingPartyId ? "is-invalid" : ""}`}
                                                                        readOnly={!isSuperAdmin()}
                                                                    />
                                                                    <ErrorMessage
                                                                        name="webIdentificationRelyingPartyId"
                                                                        component="div"
                                                                        className="invalid-feedback"
                                                                    />
                                                                </FormGroup>
                                                            </Col>
                                                        )}
                                                    </Row>

                                                    <Row className="justify-content-center">
                                                        <Col xs="auto">
                                                            <FormGroup className="text-center">
                                                                <br />
                                                                <Button
                                                                    onClick={() => handleSubmit()}
                                                                    label="Submit"
                                                                    type="button"
                                                                    value="Submit"
                                                                    loading={this.state.isLoading}
                                                                    disabled={this.state.isLoading}
                                                                    text
                                                                    rounded
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </Form>
                                            )}
                                        </Formik>
                                                </TabPanel>
                                            )}

                                <TabPanel
                                    header="General"
                                    pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                    headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                >
                                    <GeneralConfiguration merchantId={this.props.merchantId} />
                                </TabPanel>
                            </TabView>
                    )}
            </div>
        );
    }
}
